import {
  Box,
  Button,
  FormControl,
  Modal,
  Typography,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  TextField,
} from "@mui/material";
import React from "react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { serviceRequestsActions, useDispatch } from "../../state";
// Define the form values interface
interface EmailFormValues {
  subject: string;
  message: string;
  status: string;
}

// Define props for the component
interface ServiceEmailModalProps {
  open: boolean;
  onClose: () => void;
  serviceRequest?: {
    id: number;
    status: string;
    email: string;
    first_name: string;
  };
  uuid: string;
}

// Email form validation schema
const EmailSchema = Yup.object().shape({
  subject: Yup.string().required("Subject is required"),
  message: Yup.string().required("Message is required"),
  status: Yup.string().required("Status is required"),
});

export default function ServiceEmailModal({
  open,
  onClose,
  serviceRequest,
  uuid,
}: ServiceEmailModalProps) {
  const dispatch = useDispatch();
  // Initial form values
  const initialValues: EmailFormValues = {
    subject: serviceRequest ? `Re: RELI Exchange Service Request` : "",
    message: "",
    status:
      (serviceRequest?.status === "new"
        ? "in_progress"
        : serviceRequest?.status) || "",
  };

  const handleSubmit = (
    values: EmailFormValues,
    { setSubmitting }: FormikHelpers<EmailFormValues>,
  ) => {
    setSubmitting(true);
    dispatch(serviceRequestsActions.updateServiceRequest(uuid, values));
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="email-modal-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
        }}
      >
        <Typography id="email-modal-title" variant="h6" component="h2" mb={3}>
          Send Email to Client
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={EmailSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  To: {serviceRequest?.email}
                </Typography>
              </FormControl>
              <Field name="subject">
                {({ field, meta }) => (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <TextField
                      {...field}
                      label="Subject"
                      fullWidth
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  </FormControl>
                )}
              </Field>
              <Typography variant="subtitle2" gutterBottom>
                Hi {serviceRequest?.first_name},
              </Typography>
              <Field name="message">
                {({ field, meta }) => (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <TextField
                      {...field}
                      label="Message"
                      fullWidth
                      multiline
                      rows={6}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  </FormControl>
                )}
              </Field>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="email-status-label">Update Status</InputLabel>
                <Field name="status">
                  {({ field }) => (
                    <MuiSelect
                      {...field}
                      labelId="email-status-label"
                      label="Update Status"
                    >
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="pending_response">
                        Pending Client Response
                      </MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </MuiSelect>
                  )}
                </Field>
              </FormControl>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Send
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
}
